import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { plotSize } from '../../components/ParcelMap.jsx';
import { Confirm, DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatXaf, timeAgo } from '../../lib/format.js';

const VERIFY_BADGE = {
  pending: { status: 'pending', label: 'Being checked' },
  verified: { status: 'approved', label: 'Verified' },
  rejected: { status: 'rejected', label: 'Turned down' }
};

/** Saying a handover is wrong instead of accepting it. */
const DisputeModal = ({ transfer, onDone, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post(`/land/transfers/${transfer.id}/dispute`, { reason });
      toast.success('Recorded. Our team will look into it with the seller and the village chief.');
      onDone();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Something is wrong with ${transfer.reference}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="dispute-form" className="btn btn-danger" disabled={busy || reason.trim().length < 10}>
            {busy ? 'Sending...' : 'Report it'}
          </button>
        </>
      }
    >
      <p className="modal-lead">The land stays registered to the seller until this is settled.</p>
      <form id="dispute-form" onSubmit={submit}>
        <Field label="What is wrong" hint="The deed was not signed, the money was not received, the land is not what was shown...">
          <textarea rows="4" value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
};

/**
 * The land registered to this person, and land somebody has sold them that is
 * waiting on them to say they have it.
 */
const MyLand = () => {
  useTitle('My land');
  const { user } = useAuth();
  const toast = useToast();
  const land = useFetch('/land/mine');
  const [confirming, setConfirming] = useState(null);
  const [disputing, setDisputing] = useState(null);
  const [busy, setBusy] = useState(false);
  const canSell = user && ['landlord', 'manager'].includes(user.role);

  if (land.loading && !land.data) return <Spinner />;
  if (land.error) {
    return (
      <div className="container section">
        <ErrorState message={land.error} onRetry={land.reload} />
      </div>
    );
  }

  const { items, incoming } = land.data;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/land/transfers/${confirming.id}/confirm`);
      toast.success('The land is now registered to you. Only you can sell it from here on.');
      setConfirming(null);
      land.reload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'plot',
      header: 'Plot',
      render: (plot) => (
        <div className="cell-stack">
          <Link to={canSell ? `/my/land/${plot.id}` : `/land/${plot.id}`} className="cell-title">
            {plot.reference}
          </Link>
          <span className="muted">
            {plot.village}
            {plot.city ? `, ${plot.city}` : ''}
            {plot.plotNumber ? ` - plot ${plot.plotNumber}` : ''}
            {plot.layout ? ` in ${plot.layout.name}` : ''}
          </span>
          {plot.certificateNumber ? <span className="muted">Certificate {plot.certificateNumber}</span> : null}
        </div>
      )
    },
    { key: 'size', header: 'Size', render: (plot) => plotSize(plot.areaSqm) },
    {
      key: 'status',
      header: 'On the register',
      render: (plot) => (
        <div className="cell-stack">
          <StatusBadge {...VERIFY_BADGE[plot.verificationStatus]} />
          {!plot.hasSitePlan ? <span className="overdue-flag">Site plan missing</span> : null}
          {plot.verificationStatus === 'rejected' && plot.verificationNote ? <span className="muted">{plot.verificationNote}</span> : null}
          {plot.conflictOpen ? <span className="overdue-flag">Conflict reported</span> : null}
        </div>
      )
    },
    {
      key: 'sale',
      header: 'Sale',
      render: (plot) =>
        plot.forSale ? (
          <Link to={`/properties/${plot.forSale.propertyId}`}>On sale at {formatXaf(plot.forSale.price)}</Link>
        ) : plot.transferPending ? (
          <span className="muted">Waiting on the buyer</span>
        ) : (
          <span className="muted">Not on sale</span>
        )
    },
    {
      key: 'actions',
      header: '',
      render: (plot) => (
        <div className="cell-actions">
          {plot.verified ? (
            <Link to={`/land/${plot.id}`} className="btn btn-ghost btn-sm">
              History
            </Link>
          ) : null}
          {canSell && plot.verified && !plot.forSale && !plot.transferPending && !plot.conflictOpen ? (
            <Link to={`/landlord/properties/new?sell=${plot.id}`} className="btn btn-primary btn-sm">
              Sell it
            </Link>
          ) : null}
          {canSell ? (
            <Link to={`/my/land/${plot.id}`} className="btn btn-outline btn-sm">
              {plot.verified ? 'Details' : 'Finish it'}
            </Link>
          ) : null}
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My land</h1>
          <p className="muted">Plots registered to you on the land register. Only the registered owner can sell a plot.</p>
        </div>
        {canSell ? (
          <Link to="/my/land/new" className="btn btn-primary">
            Register a plot
          </Link>
        ) : null}
      </header>

      {incoming.length ? (
        <section className="panel">
          <h2>Land being handed to you</h2>
          <p className="field-hint">
            The seller says the sale is complete. Confirm only once the deed is signed before the notary and you have the
            papers - then the register names you as the owner.
          </p>
          {incoming.map((transfer) => (
            <div key={transfer.id} className="handover-row">
              <div>
                <strong>{transfer.reference}</strong>
                <span className="muted">
                  {' '}
                  {transfer.village}
                  {transfer.plotNumber ? `, plot ${transfer.plotNumber}` : ''} - {formatXaf(transfer.amount)}, {timeAgo(transfer.createdAt)}
                </span>
              </div>
              <div className="row-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setConfirming(transfer)}>
                  I have received it
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDisputing(transfer)}>
                  Something is wrong
                </button>
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {!canSell && items.length ? (
        <div className="notice notice-info">
          To sell land registered to you, you need a landlord account. Ask our team to change your account over.
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'No land registered to you',
          message: canSell
            ? 'Register a plot from the site plan its village chief signed. Once it is checked you can sell it.'
            : 'Land you buy here is registered to you once you confirm you have received it.'
        }}
      />

      {confirming ? (
        <Confirm
          title={`Take ownership of ${confirming.reference}?`}
          message="Confirm only if the deed is signed and you have the papers. The register then shows you as the owner, and the seller can no longer sell it."
          confirmLabel="Yes, I have received it"
          tone="primary"
          busy={busy}
          onConfirm={confirm}
          onClose={() => setConfirming(null)}
        />
      ) : null}
      {disputing ? (
        <DisputeModal
          transfer={disputing}
          onClose={() => setDisputing(null)}
          onDone={() => {
            setDisputing(null);
            land.reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default MyLand;
